import { useState } from 'react';
import type { SaveContextRequest } from '@attack100/shared';

interface ProblemInterviewModalProps {
  problemId: number;
  onComplete: (context: SaveContextRequest) => void;
  onSkip: () => void;
}

export function ProblemInterviewModal({ problemId: _problemId, onComplete, onSkip }: ProblemInterviewModalProps) {
  const [step, setStep] = useState(1);
  const totalSteps = 10;

  const [context, setContext] = useState<SaveContextRequest>({
    stage: undefined,
    timeline: undefined,
    resources: undefined,
    success_criteria: '',
    has_money: false,
    money_flow: undefined,
    has_users: false,
    user_count: '',
    user_needs: '',
    is_technical: false,
    tech_comfort: undefined,
    biggest_fear: '',
    competition_level: undefined,
    shipping_preference: undefined,
  });

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      onComplete(context);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const updateContext = (field: keyof SaveContextRequest, value: any) => {
    setContext({ ...context, [field]: value });
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="interview-step">
            <h3>Where are you with this problem?</h3>
            <p className="hint">This helps us understand your starting point</p>
            <div className="radio-group">
              {[
                { value: 'idea', label: 'Just an idea', desc: 'Still thinking it through' },
                { value: 'planning', label: 'Planning', desc: 'Designing the approach' },
                { value: 'building', label: 'Building', desc: 'Actively implementing' },
                { value: 'scaling', label: 'Scaling', desc: 'Growing what works' },
                { value: 'fixing', label: 'Fixing', desc: 'Solving existing issues' },
              ].map(option => (
                <label key={option.value} className="radio-option">
                  <input
                    type="radio"
                    name="stage"
                    value={option.value}
                    checked={context.stage === option.value}
                    onChange={(e) => updateContext('stage', e.target.value as any)}
                  />
                  <div>
                    <strong>{option.label}</strong>
                    <span className="hint">{option.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="interview-step">
            <h3>When do you need this solved?</h3>
            <p className="hint">Timeline affects how we prioritize angles</p>
            <div className="radio-group">
              {[
                { value: 'days', label: 'Days', desc: 'Urgent, need it now' },
                { value: 'weeks', label: 'Weeks', desc: 'Important but not rushed' },
                { value: 'months', label: 'Months', desc: 'Steady progress' },
                { value: 'years', label: 'Years', desc: 'Long-term vision' },
              ].map(option => (
                <label key={option.value} className="radio-option">
                  <input
                    type="radio"
                    name="timeline"
                    value={option.value}
                    checked={context.timeline === option.value}
                    onChange={(e) => updateContext('timeline', e.target.value as any)}
                  />
                  <div>
                    <strong>{option.label}</strong>
                    <span className="hint">{option.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="interview-step">
            <h3>What resources do you have?</h3>
            <p className="hint">Helps us understand your capacity</p>
            <div className="radio-group">
              {[
                { value: 'solo', label: 'Just me', desc: 'Solo founder/builder' },
                { value: 'small_team', label: 'Small team', desc: '2-5 people' },
                { value: 'big_team', label: 'Big team', desc: '6+ people' },
                { value: 'funded', label: 'Well funded', desc: 'Significant budget' },
              ].map(option => (
                <label key={option.value} className="radio-option">
                  <input
                    type="radio"
                    name="resources"
                    value={option.value}
                    checked={context.resources === option.value}
                    onChange={(e) => updateContext('resources', e.target.value as any)}
                  />
                  <div>
                    <strong>{option.label}</strong>
                    <span className="hint">{option.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="interview-step">
            <h3>What does winning look like?</h3>
            <p className="hint">Define success in your own words</p>
            <textarea
              className="textarea"
              value={context.success_criteria || ''}
              onChange={(e) => updateContext('success_criteria', e.target.value)}
              placeholder="Example: 'Launch MVP with 100 users' or 'Solve this technical challenge'..."
              rows={4}
            />
          </div>
        );

      case 5:
        return (
          <div className="interview-step">
            <h3>Does this involve money?</h3>
            <p className="hint">Helps The Economist understand the financial angle</p>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="has_money"
                  checked={context.has_money === true}
                  onChange={() => updateContext('has_money', true)}
                />
                <div><strong>Yes</strong></div>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="has_money"
                  checked={context.has_money === false}
                  onChange={() => updateContext('has_money', false)}
                />
                <div><strong>No</strong></div>
              </label>
            </div>
            {context.has_money && (
              <div style={{ marginTop: '1rem' }}>
                <label className="label">Are you spending, making, or both?</label>
                <select
                  className="input"
                  value={context.money_flow || ''}
                  onChange={(e) => updateContext('money_flow', e.target.value as any)}
                >
                  <option value="">Select...</option>
                  <option value="spending">Spending money</option>
                  <option value="making">Making money</option>
                  <option value="both">Both</option>
                </select>
              </div>
            )}
          </div>
        );

      case 6:
        return (
          <div className="interview-step">
            <h3>Do you have users or customers?</h3>
            <p className="hint">Helps The Psychologist understand user needs</p>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="has_users"
                  checked={context.has_users === true}
                  onChange={() => updateContext('has_users', true)}
                />
                <div><strong>Yes</strong></div>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="has_users"
                  checked={context.has_users === false}
                  onChange={() => updateContext('has_users', false)}
                />
                <div><strong>No</strong></div>
              </label>
            </div>
            {context.has_users && (
              <>
                <div style={{ marginTop: '1rem' }}>
                  <label className="label">How many?</label>
                  <input
                    type="text"
                    className="input"
                    value={context.user_count || ''}
                    onChange={(e) => updateContext('user_count', e.target.value)}
                    placeholder="e.g., '100', '1000+', 'a few'"
                  />
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <label className="label">What do they need most?</label>
                  <textarea
                    className="textarea"
                    value={context.user_needs || ''}
                    onChange={(e) => updateContext('user_needs', e.target.value)}
                    placeholder="What's their biggest pain point?"
                    rows={3}
                  />
                </div>
              </>
            )}
          </div>
        );

      case 7:
        return (
          <div className="interview-step">
            <h3>Is this technical?</h3>
            <p className="hint">Helps The Architect plan the technical approach</p>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="is_technical"
                  checked={context.is_technical === true}
                  onChange={() => updateContext('is_technical', true)}
                />
                <div><strong>Yes</strong></div>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="is_technical"
                  checked={context.is_technical === false}
                  onChange={() => updateContext('is_technical', false)}
                />
                <div><strong>No</strong></div>
              </label>
            </div>
            {context.is_technical && (
              <div style={{ marginTop: '1rem' }}>
                <label className="label">What's your tech comfort level?</label>
                <select
                  className="input"
                  value={context.tech_comfort || ''}
                  onChange={(e) => updateContext('tech_comfort', e.target.value as any)}
                >
                  <option value="">Select...</option>
                  <option value="beginner">Beginner - Still learning</option>
                  <option value="intermediate">Intermediate - Can build things</option>
                  <option value="expert">Expert - Deep technical knowledge</option>
                </select>
              </div>
            )}
          </div>
        );

      case 8:
        return (
          <div className="interview-step">
            <h3>What scares you most about this?</h3>
            <p className="hint">Helps The Red Team identify and mitigate risks</p>
            <textarea
              className="textarea"
              value={context.biggest_fear || ''}
              onChange={(e) => updateContext('biggest_fear', e.target.value)}
              placeholder="What keeps you up at night about this problem?"
              rows={4}
            />
          </div>
        );

      case 9:
        return (
          <div className="interview-step">
            <h3>Who else is solving this?</h3>
            <p className="hint">Helps The Strategist understand the competitive landscape</p>
            <div className="radio-group">
              {[
                { value: 'none', label: 'Nobody', desc: 'Blue ocean' },
                { value: 'few', label: 'A few players', desc: 'Room to differentiate' },
                { value: 'many', label: 'Many competitors', desc: 'Crowded space' },
                { value: 'giants', label: 'Big companies', desc: 'Competing with giants' },
              ].map(option => (
                <label key={option.value} className="radio-option">
                  <input
                    type="radio"
                    name="competition_level"
                    value={option.value}
                    checked={context.competition_level === option.value}
                    onChange={(e) => updateContext('competition_level', e.target.value as any)}
                  />
                  <div>
                    <strong>{option.label}</strong>
                    <span className="hint">{option.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        );

      case 10:
        return (
          <div className="interview-step">
            <h3>Fast or perfect?</h3>
            <p className="hint">Helps The Builder understand your shipping philosophy</p>
            <div className="radio-group">
              {[
                { value: 'fast', label: 'Ship fast', desc: 'Speed over polish' },
                { value: 'balanced', label: 'Balanced', desc: 'Quality + speed' },
                { value: 'perfect', label: 'Ship perfect', desc: 'Quality over speed' },
              ].map(option => (
                <label key={option.value} className="radio-option">
                  <input
                    type="radio"
                    name="shipping_preference"
                    value={option.value}
                    checked={context.shipping_preference === option.value}
                    onChange={(e) => updateContext('shipping_preference', e.target.value as any)}
                  />
                  <div>
                    <strong>{option.label}</strong>
                    <span className="hint">{option.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={onSkip}>
      <div className="modal-content interview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Problem Context Interview</h2>
          <button onClick={onSkip} className="close-btn">×</button>
        </div>

        <div className="progress-bar" style={{ marginBottom: '2rem' }}>
          <div className="progress-fill" style={{ width: `${(step / totalSteps) * 100}%` }} />
        </div>

        <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1.5rem' }}>
          Question {step} of {totalSteps}
        </div>

        {renderStep()}

        <div className="modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
          <button onClick={onSkip} className="btn btn-secondary">
            Skip Interview
          </button>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {step > 1 && (
              <button onClick={handleBack} className="btn btn-secondary">
                Back
              </button>
            )}
            <button onClick={handleNext} className="btn btn-primary">
              {step === totalSteps ? '✨ Generate Angles' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
